// Prefer the in-cluster backend URL for server-side calls, SSR runs inside the
// container where the browser-facing NEXT_PUBLIC_API_BASE_URL (localhost) is not
// the backend. INTERNAL_API_URL is undefined in the browser, so client callers
// fall through to the public URL automatically.
const API_BASE =
  process.env.INTERNAL_API_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  'http://localhost:4000';

export type ListedEvent = {
  id: string;
  slug: string;
  /** Second line in hero (all caps, wide tracking), e.g. COMMON GROUND */
  partnerLine: string;
  /** Blog-style hero image path */
  heroImage: string;
  dateLabel: string;
  dateStartISO: string;
  dateEndISO?: string;
  location: string;
  /** When set, the event is only listed for these COVA store ids. */
  covaLocationIds?: number[];
  title: string;
  description: string;
  /** Same as schedule line on detail when set server-side; optional raw override key. */
  scheduleDisplayOverride?: string;
  aboutTitle?: string;
  aboutBody?: string;
  aboutHighlights?: string[];
  aboutImage?: string;
  pastVibesTitle?: string;
  pastVibesIntro?: string;
  pastVibesImages?: string[];
  showProductSelection?: boolean;
  showReviewsSection?: boolean;
  showUpcomingEventsSection?: boolean;
};

export async function getListedEventBySlug(slug: string): Promise<ListedEvent | null> {
  try {
    const encoded = encodeURIComponent(slug);
    const res = await fetch(`${API_BASE}/api/events/slug/${encoded}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.success || !data?.event) return null;
    return data.event as ListedEvent;
  } catch {
    return null;
  }
}

export type EventsTab = 'upcoming' | 'archive';

export async function getEventsByTab(tab: EventsTab, covaLocationId?: number | null): Promise<ListedEvent[]> {
  try {
    const params = new URLSearchParams({ tab, pageSize: '50' });
    if (covaLocationId != null && Number.isFinite(covaLocationId) && covaLocationId > 0) {
      params.set('covaLocationId', String(Math.round(covaLocationId)));
    }
    const res = await fetch(`${API_BASE}/api/events?${params.toString()}`, { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    if (!data?.success || !Array.isArray(data.events)) return [];
    return data.events as ListedEvent[];
  } catch {
    return [];
  }
}

/** Hero images from published events (archive + upcoming), newest by start time first, for homepage highlights. */
export async function getEventHighlightImageUrls(max = 16, covaLocationId?: number | null): Promise<string[]> {
  try {
    const archParams = new URLSearchParams({ tab: 'archive', pageSize: '40' });
    const upParams = new URLSearchParams({ tab: 'upcoming', pageSize: '40' });
    if (covaLocationId != null && Number.isFinite(covaLocationId) && covaLocationId > 0) {
      const id = String(Math.round(covaLocationId));
      archParams.set('covaLocationId', id);
      upParams.set('covaLocationId', id);
    }
    const [archRes, upRes] = await Promise.all([
      fetch(`${API_BASE}/api/events?${archParams.toString()}`, { cache: 'no-store' }),
      fetch(`${API_BASE}/api/events?${upParams.toString()}`, { cache: 'no-store' }),
    ]);
    const merged: ListedEvent[] = [];
    for (const res of [archRes, upRes]) {
      if (!res.ok) continue;
      const data = await res.json();
      if (data?.success && Array.isArray(data.events)) merged.push(...data.events);
    }
    merged.sort(
      (a, b) => new Date(b.dateStartISO).getTime() - new Date(a.dateStartISO).getTime()
    );
    const seen = new Set<string>();
    const urls: string[] = [];
    for (const e of merged) {
      const u = (e.heroImage || '').trim();
      if (!u || seen.has(u)) continue;
      seen.add(u);
      urls.push(u);
      if (urls.length >= max) break;
    }
    return urls;
  } catch {
    return [];
  }
}
